import { useTranslations } from 'next-intl';
import { useStore } from '@/app/providers/store';
import {
  buildExtensionToolDraftFromTask,
  buildQuickEditToolDraftFromTask,
  isImageTask,
  reportEvent,
} from '@/app/utils';
import { openOrRedirect } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { RECREATE_INSPIRATION_KEY, RECREATE_INSPIRATION_SOURCE_KEY, RECREATE_TASK_KEY, SCENE } from '../constants';
import type { ReportParams, TaskInfo } from '../types';
import { getReportParams, isImageToVideoTaskType } from '../utils';
import { useProcessRecreateData } from './use-process-recreate-data';

const TOOL_RECREATE_PATH_MAP: Partial<Record<TaskInfo['type'], string>> = {
  video_edit: '/create/video-edit',
  image_edit: '/create/image-edit',
  remake: '/create/video-remake',
  one_click: '/create/one-click-video-production',
  face_swap: '/create/face-swap',
  tts: '/create/text-to-speech',
  text2audio: '/create/ai-sound-effects',
  short_story: '/create/story-grid',
  style_transfer: '/create/style-transfer',
  motion_control: '/create/motion-control',
};

/** 走 processToolRecreateData 的 taskType（其余走 processRecreateData） */
const TOOL_TASK_TYPES = new Set<TaskInfo['type']>([
  'video_edit',
  'image_edit',
  'remake',
  'one_click',
  'face_swap',
  'motion_control',
  'tts',
  'text2audio',
  'video2extend',
  'video2extail',
]);

/**
 * 检查 Id 是否有效
 * @param id
 * @returns 是否有效
 */
const checkIdValid = (id?: string) => {
  return !!id && id !== '0';
};

interface NavigateToPathParams {
  tab: TaskInfo['type'];
  creationId?: string;
  templateScene?: string;
  openNewTab?: boolean;
  inspirationId?: string;
  taskId?: string;
  source?: string;
  taskType?: TaskInfo['type'];
}

/**
 * 使用的地方
 * 1. 视频详情弹窗《做同款》按钮
 * 2. 任务列表卡片《做同款》按钮
 * 3. 主体库-主体详情-《使用》按钮
 * 4. 首页-主体-主体详情-《使用》按钮
 * 5. 主页-推荐-Feed流卡片《做同款》按钮
 */
export const useRecreate = () => {
  const { processRecreateData, processToolRecreateData } = useProcessRecreateData();
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations();
  const { userInfo, replaceQuickEditToolDraft, replaceExtensionToolDraft } = useStore((state) => ({
    userInfo: state.userInfo,
    replaceQuickEditToolDraft: state.replaceQuickEditToolDraft,
    replaceExtensionToolDraft: state.replaceExtensionToolDraft,
  }));

  const navigateToPath = ({
    tab,
    creationId,
    templateScene,
    openNewTab,
    inspirationId,
    taskId,
    source,
    taskType,
  }: NavigateToPathParams) => {
    let redirectPath = `/create/${tab}`;

    if (taskType === 'video2extend' || taskType === 'video2extail') {
      if (openNewTab && creationId && checkIdValid(creationId)) {
        const params = new URLSearchParams();
        params.set('creationId', creationId);
        redirectPath = `/create/extension?${params.toString()}`;
      } else {
        redirectPath = '/create/extension';
      }
    } else if (taskType && TOOL_RECREATE_PATH_MAP[taskType]) {
      redirectPath = TOOL_RECREATE_PATH_MAP[taskType];
    } else if (tab === SCENE) {
      redirectPath = `/templates/${templateScene}`;
    }

    const shouldAppendRecreateParams = !!openNewTab || !taskType || !TOOL_TASK_TYPES.has(taskType);
    let targetUrl = redirectPath;

    if (shouldAppendRecreateParams && (checkIdValid(taskId) || checkIdValid(inspirationId))) {
      const [targetPath, queryString = ''] = redirectPath.split('?');
      const params = new URLSearchParams(queryString);

      // 如果有灵感id 代表需要归因再创作灵感来源，走灵感id+source归因流程
      if (checkIdValid(taskId) && !inspirationId) {
        params.set(RECREATE_TASK_KEY, taskId || '');
      } else {
        params.set(RECREATE_INSPIRATION_KEY, inspirationId || '');
        params.set(RECREATE_INSPIRATION_SOURCE_KEY, source ?? 'inspiration');
      }

      const finalQueryString = params.toString();
      targetUrl = finalQueryString ? `${targetPath}?${finalQueryString}` : targetPath;
    }

    if (openNewTab) {
      openOrRedirect(targetUrl);
      return;
    }
    if (targetUrl === pathname) return;
    router.push(targetUrl);
  };

  const handleRecreate = async ({
    taskId,
    creationId,
    taskType,
    settings,
    input,
    templateScene,
    traceId,
    interactionPosition,
    openNewTab,
    inspirationId,
    source,
    reportParams,
  }: {
    taskId: string; //任务id；非自己作品，获取到的 taskId 可能是 '0'
    creationId: string; //生成物 id
    taskType: TaskInfo['type']; //任务类型
    settings?: TaskInfo['settings']; //任务设置
    input: TaskInfo['input']; //输入参数
    templateScene?: string; //模板场景
    fromInspiration?: boolean; //是否是来自灵感
    traceId?: string; // 追踪ID
    interactionPosition?: string; //交互位置
    openNewTab?: boolean; //是否新窗口打开
    inspirationId?: string; //灵感id; 新窗口打开 && 灵感做同款时，inspirationId必传
    source?: 'share' | 'inspiration' | 'others';
    reportParams?: ReportParams;
  }) => {
    if (inspirationId) {
      reportEvent('inspiration_interaction', {
        trace_id: traceId || '',
        creation_id: inspirationId,
        interaction_type: 'recreate',
        interaction_position: interactionPosition || pathname,
        content_type: isImageTask({ type: taskType }) ? 'image' : 'video',
      });
    } else {
      reportEvent('operation', {
        interaction_type: 'recreate',
        interaction_position: interactionPosition || pathname,
        task_id: taskId,
        creation_id: creationId,
        ...getReportParams(taskType, settings, templateScene),
        ...reportParams,
      });
    }

    const tab = isImageToVideoTaskType(taskType) ? 'img2video' : taskType;
    const isToolTask = TOOL_TASK_TYPES.has(taskType);
    const isQuickEditTask = taskType === 'video_edit' || taskType === 'image_edit';
    const isExtensionTask = taskType === 'video2extend' || taskType === 'video2extail';

    if (isToolTask) {
      if (isQuickEditTask && !openNewTab) {
        const quickEditMode = taskType === 'video_edit' ? 'video' : 'image';
        const draft = buildQuickEditToolDraftFromTask({
          mode: quickEditMode,
          task: { input, settings },
          taskId: checkIdValid(taskId) ? taskId : creationId,
          locale,
          userPlan: userInfo?.subs_plan,
          originalVideoName: t('create.createForm.originalVideoName'),
          originalImageName: t('create.createForm.originalImageName'),
        });

        if (draft) {
          replaceQuickEditToolDraft(quickEditMode, draft);
        }
      } else if (isExtensionTask && !openNewTab) {
        if (!checkIdValid(taskId)) {
          return;
        }

        const draft = buildExtensionToolDraftFromTask({
          task: { input, settings },
          taskId,
        });

        if (draft) {
          replaceExtensionToolDraft(draft);
        }
      } else {
        await processToolRecreateData({
          taskType,
          settings,
          input,
        });
      }

      navigateToPath({ tab, creationId, templateScene, openNewTab, inspirationId, taskId, source, taskType });
      return;
    }

    processRecreateData({
      tab,
      settings,
      input,
      templateScene,
      creationId,
      inspirationId,
      source,
      taskType,
    });

    navigateToPath({ tab, creationId, templateScene, openNewTab, inspirationId, taskId, source, taskType });
  };

  return {
    handleRecreate,
  };
};
